import Swal from 'sweetalert2';

/**
 * Some useful utility functions cribbed wholesale from underscore.js
 */

const { forEach } = Array.prototype;

function Util() {
    // This is the class.
}

/**
 * Test if this is a Javascript object
 * @param obj
 * @returns {boolean}
 */
Util.prototype.isObject = function (obj) {
    const type = typeof obj;
    return type === 'function' || (type === 'object' && !!obj);
};

/**
 * Checks if the object is a native Javascript array
 * @param {Object} obj
 * @returns {boolean} true if the object is an Array, false otherwise.
 */
Util.prototype.isArray = function (obj) {
    return Object.prototype.toString.call(obj) === '[object Array]'
        || obj instanceof Array;
};

/**
 * Test this object to check if it's a DOM element
 * @param {Object} obj
 * @returns {Boolean} true if this is a DOM element, false otherwise.
 */
Util.prototype.isElement = function (obj) {
    return !!(obj && obj.nodeType === 1);
};

/**
 * Test this object to see if it's a NodeList, or alternatively an Array
 * containing Element objects, since that's functionally equivalent.
 * @param obj
 */
Util.prototype.isElements = function (obj) {
    const self = this;
    if (obj instanceof NodeList) {
        return true;
    }
    if (self.isArray(obj)) {
        let allElements = true;
        forEach.call(obj, (element) => {
            if (!self.isElement(element)) {
                allElements = false;
            }
        });
        return allElements;
    }
    return false;
};

Util.prototype.fireDialogBox = async function ({
    title,
    message,
    confirmBtnLabel,
    cancelBtnLabel,
    destructive = false,
}) {
    const primaryBtn = !destructive ? 'btn btn-primary' : 'btn btn-danger me-2';
    return Swal.fire({
        allowOutsideClick: false,
        backdrop: 'rgba(0, 0, 0, 0.2)',
        confirmButtonText: confirmBtnLabel,
        html: message,
        showCancelButton: true,
        cancelButtonText: cancelBtnLabel,
        customClass: {
            title: 'text-start',
            htmlContainer: 'text-start',
            confirmButton: primaryBtn,
            cancelButton: 'btn btn-outline-secondary me-2',
            actions: 'ms-auto me-3 mt-3',
        },
        buttonsStyling: false,
        title,
        width: '35rem',
        reverseButtons: !destructive,
    });
};

Util.prototype.fireThreeButtonDialogBox = async function ({
    title,
    message,
    confirmBtnLabel,
    middleBtnLabel,
    cancelBtnLabel,
    destructive = false,
}) {
    const primaryBtn = !destructive ? 'btn btn-primary' : 'btn btn-danger';
    return Swal.fire({
        allowOutsideClick: false,
        backdrop: 'rgba(0, 0, 0, 0.2)',
        confirmButtonText: confirmBtnLabel,
        confirmButtonAriaLabel: confirmBtnLabel,
        showDenyButton: true,
        showCancelButton: true,
        cancelButtonText: cancelBtnLabel,
        cancelButtonAriaLabel: cancelBtnLabel,
        denyButtonText: middleBtnLabel,
        denyButtonAriaLabel: middleBtnLabel,
        html: message,
        customClass: {
            title: 'text-start',
            htmlContainer: 'text-start',
            confirmButton: primaryBtn,
            cancelButton: 'btn btn-outline-secondary me-2',
            denyButton: 'btn btn-outline-secondary me-2',
            actions: 'ms-auto me-3 mt-3',
        },
        buttonsStyling: false,
        title,
        width: '35rem',
        reverseButtons: !destructive,
    });
};

/**
 * Extend obj with the properties of one or more other objects, e.g.
 *
 * var myObj = {
 *   person: 'Bob'
 * };
 *
 * util.extend(myObj, {age: 25}, {gender: 'Male'});
 * // { person: 'Bob', age: 25, gender: 'Male'}
 * @param {Object} obj
 * @returns {Object} obj, extended with additional properties
 */
Util.prototype.extend = function (obj, ...args) {
    if (!this.isObject(obj)) return obj;
    forEach.call(args, (source) => {
        Object.keys(source || {}).forEach((prop) => {
            obj[prop] = source[prop];
        });
    });
    return obj;
};

Util.prototype.upload = function (node, callback) {
    let valid = false;
    let file;
    const data = new FormData();

    if (node.files && node.files[0]) {
        file = node.files[0];
        if (typeof file === 'undefined') return;
        valid = file !== 'undefined' && this.validateFileSize(node, 10);
        data.append('FILE_NAME.FILE_UPLOAD', file);
    } else if (node.data) {
        const dividedFilename = node.filename.split('.');
        const fileExtension = dividedFilename.pop().toUpperCase();

        // the croppie library can not handle .jpeg extension
        if (fileExtension === 'JPEG') {
            node.filename = `${dividedFilename.join('.')}.jpg`;
        }

        data.append('FILE_NAME.FILE_UPLOAD', node.data, node.filename);
        valid = true;
    }

    if (valid) {
        ajax({
            url: 'files/upload',
            method: 'POST',
            formData: data,
            documentType: 'json',
        }, (response, status) => {
            if (!response) {
                notification.notify('Error', 'An unexpected error occurred.', -1);
                callback(true);
            } else if (response.status.value < 0 || status !== 200) {
                const text = response.status.text || 'An unexpected error occurred.';
                notification.notify('Error', text, response.status.value);
                callback(true);
            } else {
                callback(null, response.key);
            }
        });
    } else {
        notification.notify('Error', 'File size is too big. Maximum file size is 10Mb. Please select another file.', -1);
        callback(true);
    }
};

Util.prototype.hasExtension = function (input, exts) {
    const fileName = input.value;
    return (new RegExp(`(${exts.join('|').replace(/\./g, '\\.')})$`)).test(fileName);
};

Util.prototype.validateFileSize = function (element, maxFileSize) {
    return element.files && element.files[0] && element.files[0].size < 1024 * 1024 * maxFileSize;
};

/**
 * Walks an element's parentNode tree until it finds a parent element matching the given CSS3
 * selector. If no matching parent is found, returns undefined.
 */
Util.prototype.parent = function (element, selector) {
    let currentElement = element;

    while (currentElement.parentNode !== document.body) {
        const parent = currentElement.parentNode;

        if (
            (parent.matches && parent.matches(selector))
            || (parent.msMatchesSelector && parent.msMatchesSelector(selector))
            || (parent.webkitMatchesSelector && parent.webkitMatchesSelector(selector))
        ) {
            return parent;
        }

        currentElement = parent;
    }

    return undefined;
};

/**
 * Extracts the key/value pairs from a url's query params.
 *
 * Eg:
 *     var location = window.location; // http://acme.com/?asdf=123#a=1&b=2
 *     parseQueryParams(location) // => {asdf: '123'}
 */
Util.prototype.parseQueryParams = function (locationObj) {
    const params = {};
    // Return empty object if there's no search
    if (!locationObj.search) {
        return params;
    }
    const keyVals = locationObj.search.substr(1).split('&');
    keyVals.forEach((keyVal) => {
        keyVal = keyVal.split('=');
        const key = keyVal[0];
        const val = keyVal[1];
        params[key] = val;
    });
    return params;
};

/**
 * Updates the browser's hash params!!!
 *
 * @param {[type]} locationObj [description]
 * @param {[type]} key         [description]
 * @param {[type]} value       [description]
 */
Util.prototype.updateHashQueryParam = function (locationObj, key, value) {
    const params = this.parseHashQueryParams(locationObj);
    params[key] = value;
    const paramsList = [];

    Object.keys(params).forEach((k) => {
        const v = params[k];
        if (v === '') return; // ie: continue
        paramsList.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    });

    let destinationUrl = [locationObj.origin, locationObj.pathname, locationObj.search];
    if (paramsList.length) {
        destinationUrl = destinationUrl.concat(['#', paramsList.join('&')]);
    }
    destinationUrl = destinationUrl.join('');

    const wl = window.location;
    const currentPage = [wl.origin, wl.pathname, wl.search].join('');

    const stayOnPage = destinationUrl.indexOf(currentPage) === 0;

    if (stayOnPage && window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, destinationUrl);
    } else {
        window.util.traceWindowLocation();
        locationObj.hash = paramsList.join('&');
    }

    return locationObj;
};

Util.prototype.addHashParam = function (href, key, value) {
    const params = this.parseHashQueryParams(href);
    href = href.split('#')[0]; // Get rid of hash so we can replace it
    params[key] = value;
    const paramsList = [];
    Object.keys(params).forEach((keyItem) => {
        paramsList.push(`${keyItem}=${params[keyItem]}`);
    });
    return `${href}#${paramsList.join('&')}`;
};

Util.prototype.removeHashParam = function (href, key) {
    const params = this.parseHashQueryParams(href);
    href = href.split('#')[0]; // Get rid of hash so we can replace it
    delete params[key];
    const paramsList = [];

    Object.keys(params).forEach((keyItem) => {
        paramsList.push(`${keyItem}=${params[keyItem]}`);
    });
    if (paramsList.length === 0) {
        return href;
    }
    return `${href}#${paramsList.join('&')}`;
};

const _eventsCache = {};

/**
 * Sugar for adding event listeners to a querySelectorAll result.
 * @param nodeList {NodeList|Node} a list of HTML elements, usually the
 * output of a document.querySelectorAll call.
 * @param event {String} DOM event you want to attach a listener to.
 * @param listener {Function} Standard event listener callback with the
 * signature `function(event)`
 */
Util.prototype.on = function (nodeList, event, listener) {
    if (!('length' in nodeList)) {
        nodeList = [nodeList];
    }
    Array.prototype.forEach.call(nodeList, (element) => {
        element.addEventListener(event, listener);
        if (!_eventsCache[element]) {
            _eventsCache[element] = {};
        }
        if (!_eventsCache[element][event]) {
            _eventsCache[element][event] = [];
        }
        _eventsCache[element][event].push(listener);
    });
    return nodeList;
};

/**
 * Sugar for removing event listeners from a querySelectorAll result. This
 * function is similar to jQuery's off function. There are two optional
 * parameters, [event] and [listener]. If you pass event but no listener,
 * off will remove all listeners for that event from the elements. If you
 * pass a listener but no events, that listener will be removed. If you
 * pass neither, all listeners for all events on the object will be
 * removed.
 * @param nodeList {NodeList|Node} a list of HTML elements, usually the
 * output of a document.querySelectorAll call.
 * @param [event] {String} optional string identifying the event type to
 * remove listeners for
 */
Util.prototype.off = function (nodeList, ...args) {
    let event;
    let listener;

    if (!('length' in nodeList)) {
        nodeList = [nodeList];
    }
    if (args.length === 1 && typeof args[0] === 'function') {
        listener = args[0];
    }
    if (args.length === 1 && typeof args[0] === 'string') {
        event = args[0];
    }
    if (args.length === 2) {
        event = args[0];
        listener = args[1];
    }
    Array.prototype.forEach.call(nodeList, (element) => {
        if (!_eventsCache[element]) {
            return;
        }
        if (event && !_eventsCache[element][event]) {
            return;
        }
        if (listener && event) {
            element.removeEventListener(event, listener);
        } else if (listener) {
            _removeListener(element, listener);
        } else if (event) {
            _removeListeners(element, event);
        } else {
            _removeListeners(element);
        }
    });
};

function _removeListeners(element, event) {
    let listener;
    Object.keys(_eventsCache[element]).forEach((ev) => {
        if (event && ev !== event) {
            return;
        }
        for (let i = 0; i < _eventsCache[element][ev].length; i++) {
            listener = _eventsCache[element][ev][i];
            element.removeEventListener(ev, listener);
        }
    });
}

function _removeListener(element, listener) {
    Object.keys(_eventsCache[element]).forEach((event) => {
        for (let i = 0; i < _eventsCache[element][event].length; i++) {
            element.removeEventListener(event, listener);
        }
    });
}

/**
 * Removes everything after the last slash, just like the unix util dirname;
 */
Util.prototype.urlCurrentDirname = function (url) {
    url = url || String(window.location);
    return url.replace(/[?#].*$/, '').replace(/\/[^\/]*$/, '/');
};

/**
 * Removes everything after the last slash, just like the unix util dirname;
 */
Util.prototype.urlBase = function () {
    const base = document.querySelector('base');

    if (!base) {
        return '';
    }

    return base.href || '';
};

/**
 * Extracts the key/value pairs from a url's hash params.
 *
 * Eg:
 *     var location = window.location; // http://acme.com/?asdf=123#a=1&b=2
 *     parseHashQueryParams(location) // => {a: '1', b: '2'}
 */
Util.prototype.parseHashQueryParams = function (locationObj) {
    const params = {};
    // Return empty object if there's no search
    if (!locationObj.hash) {
        return params;
    }
    const keyVals = locationObj.hash.substr(1).split('&');
    keyVals.forEach((keyVal) => {
        keyVal = keyVal.split('=');
        const key = keyVal[0];
        const val = keyVal[1];
        params[key] = val;
    });
    return params;
};

Util.prototype.toDateInputValue = function (obj) {
    let local = new Date();

    if (obj) {
        local = obj;
    }

    local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
    return local.toJSON().slice(0, 10);
};

Util.prototype.scrollIntoView = function (what) {
    const selecteds = document.querySelectorAll(what);

    for (let idx = 0; idx < selecteds.length; idx++) {

        const selected = selecteds[idx];

        const parentWidth = selected.parentNode.clientWidth;
        const centre = (parentWidth / 2) - (selected.clientWidth / 2);
        const selectedOffset = selected.offsetLeft;
        const scrollPos = selectedOffset - centre;
        selected.parentNode.scrollLeft = scrollPos;
    }
};

Util.prototype.each = (list, pred) => {
    for (let idx = 0; idx < list.length; idx++) {
        const item = list[idx];
        pred(item, idx, list);
    }
};

/**
 * Initialise DOM elements after page is loaded and when nodes are added.
 *
 * @param {Function} handler   The callback function with the signature
 *                             handler (addedNodes).
 *
 *     @param {Array<DOM Node>} addedNodes
 *            Contains the nodes added directly to options.observee, or
 *            in the case of the page being loaded (DOMContentLoaded) an array
 *            containing just document.body.
 *
 * @param {Object} options     Optional.
 *
 * @param {DOMElement} options.observee
 *                             Defaults to document.body
 */
Util.prototype.initialiseAddedNodes = function (handler, options = {}) {
    let   observee = options.observee || document.body;
    const filter   = options.filter   || ((r) => r.addedNodes && r.addedNodes.length);

    function mutationHandler(aMutationRecords) {

        aMutationRecords.filter(filter);

        let addedNodes = [];

        Array.from(aMutationRecords).forEach((mutationRecord) => {
            addedNodes = addedNodes.concat(Array.from(mutationRecord.addedNodes));
        });

        addedNodes = addedNodes.filter((n) => !!n.querySelectorAll);

        if (addedNodes.length) {
            handler(addedNodes);
        }
    }

    function initAndObserve() {
        handler([document.body]);

        if (typeof observee === 'string') {
            const ob = $(observee)[0];

            if (!ob) return;

            observee = ob;
        }

        const observer = new MutationObserver(mutationHandler);

        observer.observe(observee, { childList: true, subtree: true });
    }

    if (document.readyState === 'complete' || document.readyState === 'loaded') { // DOMContentLoaded has fired
        initAndObserve();
    } else {
        document.addEventListener('DOMContentLoaded', () => {
            initAndObserve();
        });
    }
};

Util.prototype.bindListeners = function (fieldHandlers) {
    fieldHandlers.forEach((specs) => {
        const [selector, event, handler] = specs;
        const searchElement = specs[3] || document;

        Array.from(searchElement.querySelectorAll(selector)).forEach((element) => {
            element.addEventListener(event, handler);
        });
    });
};

/**
 * Open url in a new window if the user has the command (macOs) or control (windows/linux/etc
 * modifier pressed.
 */
Util.prototype.openInNewWindowIf = function (evt, url) {
    if (evt.metaKey || evt.ctrlKey || evt.button === 1) {
        const win = window.open(url, '_blank');
        if (evt.shiftKey) win.focus();
        return true;
    }

    return false; // indicate to caller that we have not handled the click
};

/**
 * There is no way to debug where a window.location = was called from.
 *
 * From now on, use this method.
 */
Util.prototype.traceWindowLocation = function () {
    return 123;
};

/**
 * Shared function for workflow(used by n900.js and n902.js)
 *
 *  @param type {String} typeof N900 or N902
 */
Util.prototype.checkAddMoreRecipients = function (type) {
    const moreRecipientsInput = document.getElementById(`${type}_EXTRA_NOTIFICATIONS_USERID`);

    if (moreRecipientsInput && moreRecipientsInput.value !== '') {
        const extraNotificationMsg = document.getElementById('extraNotificationMsg');

        document.getElementById(`${type}_EXTRA_NOTIFICATIONS_USERID`).classList.add('form-control-danger');
        extraNotificationMsg.querySelector('p').textContent = `Do you want to add ${moreRecipientsInput.value} as a recipient?`;
        Util.prototype.showElement(extraNotificationMsg);

        return false;
    }

    return true;
};

/**
 * Shows or hides element based on show argument.
 * Uses bootstrap class d-none (display-none).
 * @param {*} el Element to show or hide.
 * @param {*} show Show (true) or hide (false).
 */
Util.prototype.showElement = function (el, show = true) {
    if (!el) return;
    if (show) el.classList.remove('d-none');
    else el.classList.add('d-none');
};

/**
 * If element requires to be flex, than we need to change d-none(display-none)
 * to type flexbox.
 * @param {*} el Element to show/hide.
 * @param {*} show Show the element true/false
 */
Util.prototype.showElementWithFlex = function (el, show = true) {
    if (show) {
        el.classList.remove('d-none');
        el.classList.add('d-flex');
    }  else {
        el.classList.add('d-none');
        el.classList.remove('d-flex');
    }
};

/**
 * Checks if HTML element is visible.
 * @param {*} el Web element
 * @returns true if shown, false if hidden
 */
Util.prototype.isElementVisible = function (el) {
    return !el.classList.contains('d-none');
};

const util = new Util();
export default util;
window.util = util;
